import { useEffect, useRef, useState } from 'react'
import L from 'leaflet'
import { TILE_PROVIDERS, MAX_ZOOM, TILE_SIZE, KEEP_BUFFER, DEFAULT_CENTER } from '../constants'
import type { Position } from '../../../../types'

// Fix Leaflet default icon issue
delete (L.Icon.Default.prototype as any)._getIconUrl
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
})

interface UseMapInitializationProps {
  mapType: 'osm' | 'satellite' | 'terrain' | 'dark'
  position: Position | null
  savedMapView?: { center: [number, number]; zoom: number } | null
  onMapReady?: (map: L.Map) => void
  onMapClick?: (lat: number, lon: number) => void
}

export const useMapInitialization = ({
  mapType,
  position,
  savedMapView,
  onMapReady,
  onMapClick,
}: UseMapInitializationProps) => {
  const mapRef = useRef<L.Map | null>(null)
  const mapContainerRef = useRef<HTMLDivElement>(null)
  const tileLayerRef = useRef<L.TileLayer | null>(null)
  const isInitializedRef = useRef(false)
  const [isLoadingTiles, setIsLoadingTiles] = useState(true)
  const loadingTimeoutRef = useRef<number | null>(null)
  const tilesLoadingCountRef = useRef(0)

  // Initialize map once
  useEffect(() => {
    if (!mapContainerRef.current || isInitializedRef.current) return

    const container = mapContainerRef.current
    if (container.offsetWidth === 0 || container.offsetHeight === 0) {
      const checkSize = setInterval(() => {
        if (container.offsetWidth > 0 && container.offsetHeight > 0) {
          clearInterval(checkSize)
          initializeMap()
        }
      }, 50)
      return () => clearInterval(checkSize)
    }

    initializeMap()

    function initializeMap() {
      if (!mapContainerRef.current || isInitializedRef.current) return

      try {
        const map = L.map(mapContainerRef.current, {
          zoomControl: false,
          attributionControl: false,
          preferCanvas: false,
          doubleClickZoom: true,
          scrollWheelZoom: true,
        })

        const tileUrl = TILE_PROVIDERS[mapType]
        const tileLayer = L.tileLayer(tileUrl, {
          maxZoom: MAX_ZOOM,
          attribution: '© OpenStreetMap contributors',
          tileSize: TILE_SIZE,
          updateWhenZooming: false,
          updateWhenIdle: true,
          keepBuffer: KEEP_BUFFER,
          maxNativeZoom: MAX_ZOOM,
        })

        tilesLoadingCountRef.current = 0
        const initialLoadCompleteRef = { current: false }

        const handleTileLoading = () => {
          tilesLoadingCountRef.current++
          if (!initialLoadCompleteRef.current) {
            requestAnimationFrame(() => {
              setIsLoadingTiles(true)
            })
          }
          if (loadingTimeoutRef.current) {
            clearTimeout(loadingTimeoutRef.current)
          }
        }

        const handleTileLoad = () => {
          tilesLoadingCountRef.current = Math.max(0, tilesLoadingCountRef.current - 1)
          if (tilesLoadingCountRef.current === 0 && !initialLoadCompleteRef.current) {
            initialLoadCompleteRef.current = true
            loadingTimeoutRef.current = window.setTimeout(() => {
              requestAnimationFrame(() => {
                setIsLoadingTiles(false)
              })
            }, 300)
          }
        }

        const handleTileError = () => {
          tilesLoadingCountRef.current = Math.max(0, tilesLoadingCountRef.current - 1)
          if (tilesLoadingCountRef.current === 0 && !initialLoadCompleteRef.current) {
            initialLoadCompleteRef.current = true
            loadingTimeoutRef.current = window.setTimeout(() => {
              requestAnimationFrame(() => {
                setIsLoadingTiles(false)
              })
            }, 500)
          }
        }

        tileLayer.on('loading', handleTileLoading)
        tileLayer.on('load', handleTileLoad)
        tileLayer.on('tileerror', handleTileError)

        if (isLoadingTiles === false) {
          setIsLoadingTiles(true)
        }

        tileLayer.addTo(map)
        tileLayerRef.current = tileLayer

        // Priority order when NOT tracking:
        // 1. savedMapView (from localStorage) - user's last viewed location
        // 2. position (GPS) - if no saved view
        // 3. DEFAULT_CENTER - fallback
        // When tracking starts, useMapFollow will override this
        if (savedMapView) {
          map.setView(savedMapView.center, savedMapView.zoom)
        } else if (position) {
          map.setView([position.lat, position.lon], 15)
        } else {
          map.setView(DEFAULT_CENTER, 12)
        }

        mapRef.current = map
        isInitializedRef.current = true

        map.whenReady(() => {
          setTimeout(() => {
            if (mapRef.current) {
              mapRef.current.invalidateSize(false)
              onMapReady?.(mapRef.current)
            }
          }, 100)
        })
      } catch (error) {
        console.error('Error initializing map:', error)
        isInitializedRef.current = false
      }
    }

    return () => {
      if (loadingTimeoutRef.current) {
        clearTimeout(loadingTimeoutRef.current)
      }
      if (mapRef.current) {
        mapRef.current.remove()
        mapRef.current = null
        isInitializedRef.current = false
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onMapReady, onMapClick])

  return {
    mapRef,
    mapContainerRef,
    tileLayerRef,
    isInitializedRef,
    isLoadingTiles,
    setIsLoadingTiles,
    tilesLoadingCountRef,
    loadingTimeoutRef,
  }
}
